<template>
  <div class="conceder-creditos">
    <!-- Passo 1: formulário -->
    <!-- div (e não form): o core/Button não define type e viraria submit -->
    <div v-if="etapa === 'formulario'">
      <Select
        filtered
        :options="cfcOptions"
        legend="Autoescola / Autônomo"
        placeholder="Busque pelo nome"
        :modelValue="schema"
        @update:modelValue="onSelecionarCfc"
        :disabled="enviando"
      />

      <div v-if="schema" class="saldo-card mt-3">
        <div class="saldo-card__cabecalho">
          <span class="saldo-card__nome">{{ cfcSelecionadoNome }}</span>
          <Badge v-if="cfcSelecionadoAutonomo" text="Autônomo" color="warning" />
        </div>
        <div v-if="carregandoSaldo" class="saldo-card__loading"><LoadingSpinner /></div>
        <div v-else class="saldo-card__saldo">
          Saldo atual: <strong>{{ saldoAtual }} NC</strong>
        </div>
      </div>

      <div v-if="tiposCreditoOptions.length > 1" class="mt-3">
        <Select
          :options="tiposCreditoOptions"
          legend="Tipo de crédito"
          placeholder="Selecione o tipo de crédito"
          :modelValue="creditoId"
          @update:modelValue="creditoId = $event"
          :disabled="enviando"
        />
      </div>

      <div class="campo mt-3">
        <label class="campo__legenda" for="concessao-quantidade">Quantidade de créditos</label>
        <div class="quantidade" :class="{ 'quantidade--erro': erroQuantidade }">
          <button type="button" class="quantidade__btn" @click="alterarQuantidade(-1)"
                  :disabled="enviando || quantidadeNumero <= 1" aria-label="Diminuir quantidade">−</button>
          <input
            id="concessao-quantidade"
            class="quantidade__input"
            type="number"
            inputmode="numeric"
            min="1"
            :max="maxConcessao"
            step="1"
            v-model="quantidade"
            :disabled="enviando"
          />
          <button type="button" class="quantidade__btn" @click="alterarQuantidade(1)"
                  :disabled="enviando || quantidadeNumero >= maxConcessao" aria-label="Aumentar quantidade">+</button>
        </div>
        <small v-if="erroQuantidade" class="campo__erro">{{ erroQuantidade }}</small>
        <small v-else class="campo__ajuda">Máximo de {{ maxConcessao }} créditos por concessão.</small>
      </div>

      <div class="campo mt-3">
        <label class="campo__legenda" for="concessao-motivo">Motivo da concessão</label>
        <div class="sugestoes">
          <button v-for="sugestao in sugestoesMotivo" :key="sugestao" type="button" class="sugestoes__chip"
                  :disabled="enviando" @click="aplicarSugestao(sugestao)">{{ sugestao }}</button>
        </div>
        <textarea
          id="concessao-motivo"
          class="motivo"
          :class="{ 'motivo--erro': motivoTocado && erroMotivo }"
          v-model="motivo"
          maxlength="500"
          rows="3"
          placeholder="Descreva por que os créditos estão sendo concedidos (ex.: negociação, protocolo, responsável comercial)."
          :disabled="enviando"
          @blur="motivoTocado = true"
        ></textarea>
        <div class="motivo__rodape">
          <small class="campo__erro">{{ motivoTocado ? erroMotivo : '' }}</small>
          <small class="campo__ajuda">{{ motivoLimpo.length }}/500</small>
        </div>
      </div>

      <div v-if="schema && !carregandoSaldo && quantidadeValida" class="previa mt-3">
        <span>Saldo atual <strong>{{ saldoAtual }}</strong></span>
        <span class="previa__seta">→</span>
        <span>Saldo após <strong>{{ saldoAtual + quantidadeNumero }}</strong></span>
        <span class="previa__delta">(+{{ quantidadeNumero }})</span>
      </div>

      <p v-if="erroApi" class="erro-api mt-3">{{ erroApi }}</p>

      <div class="acoes mt-4">
        <Button text="Cancelar" color="secondary" :fill="false" @handle:click="$emit('cancelar')" />
        <Button text="Revisar concessão" color="primary" :disabled="!formularioValido" @handle:click="revisar" />
      </div>
    </div>

    <!-- Passo 2: confirmação -->
    <div v-else class="confirmacao">
      <h4 class="confirmacao__titulo">Confirme a concessão</h4>
      <dl class="resumo">
        <div class="resumo__item"><dt>Autoescola / Autônomo</dt><dd>{{ cfcSelecionadoNome }}</dd></div>
        <div v-if="tiposCreditoOptions.length > 1" class="resumo__item"><dt>Tipo de crédito</dt><dd>{{ creditoSelecionadoTexto }}</dd></div>
        <div class="resumo__item"><dt>Quantidade</dt><dd>+{{ quantidadeNumero }} NC</dd></div>
        <div class="resumo__item"><dt>Saldo</dt><dd>{{ saldoAtual }} → <strong>{{ saldoAtual + quantidadeNumero }}</strong> NC</dd></div>
        <div class="resumo__item resumo__item--bloco"><dt>Motivo</dt><dd>{{ motivoLimpo }}</dd></div>
      </dl>
      <p class="aviso">
        <i class="fa fa-info-circle"></i>
        Esta operação fica registrada no seu usuário e não pode ser desfeita pela tela.
      </p>

      <p v-if="erroApi" class="erro-api">{{ erroApi }}</p>

      <div class="acoes mt-4">
        <Button text="Voltar" color="secondary" :fill="false" :disabled="enviando" @handle:click="voltar" />
        <Button :text="enviando ? 'Concedendo...' : textoConfirmar" color="primary"
                :disabled="enviando" @handle:click="confirmar" />
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/components/core/Button.vue'
import Select from '@/components/core/Select.vue'
import Badge from '@/components/core/Badge.vue'
import LoadingSpinner from '@/components/core/LoadingSpinner.vue'
import urlApi from '@/router/urlApiV2'
import { isSchemaAutonomo } from '@/constants/userRoles'

// Deve acompanhar config('creditos.concessao_max') no backend (que é quem valida de fato)
const MAX_CONCESSAO = 500

export default {
  name: 'ConcederCreditos',
  components: { Button, Select, Badge, LoadingSpinner },
  props: {
    // [{ value: tenant_schema, text: nome_fantasia }]
    cfcOptions: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      etapa: 'formulario',
      schema: '',
      creditoId: '',
      quantidade: 1,
      motivo: '',
      motivoTocado: false,
      saldos: [],
      tiposCreditoOptions: [],
      carregandoSaldo: false,
      enviando: false,
      erroApi: '',
      maxConcessao: MAX_CONCESSAO,
      sugestoesMotivo: [
        'Cortesia comercial',
        'Compensação por falha no sistema',
        'Negociação comercial',
      ],
    }
  },
  computed: {
    quantidadeNumero () {
      const n = Number(this.quantidade)
      return Number.isInteger(n) ? n : NaN
    },
    erroQuantidade () {
      if (this.quantidade === '' || this.quantidade === null) return 'Informe a quantidade.'
      if (!Number.isInteger(this.quantidadeNumero)) return 'A quantidade deve ser um número inteiro.'
      if (this.quantidadeNumero < 1) return 'A quantidade deve ser no mínimo 1.'
      if (this.quantidadeNumero > this.maxConcessao) return `A quantidade máxima por concessão é ${this.maxConcessao}.`
      return ''
    },
    quantidadeValida () {
      return !this.erroQuantidade
    },
    motivoLimpo () {
      return (this.motivo || '').trim()
    },
    erroMotivo () {
      if (!this.motivoLimpo) return 'Informe o motivo da concessão.'
      if (this.motivoLimpo.length < 10) return 'O motivo deve ter no mínimo 10 caracteres.'
      return ''
    },
    formularioValido () {
      return !!this.schema && !!this.creditoId && !this.carregandoSaldo &&
        this.quantidadeValida && !this.erroMotivo && !this.enviando
    },
    cfcSelecionado () {
      return this.cfcOptions.find(o => o.value === this.schema) || null
    },
    cfcSelecionadoNome () {
      return this.cfcSelecionado ? this.cfcSelecionado.text : ''
    },
    cfcSelecionadoAutonomo () {
      return isSchemaAutonomo(this.schema)
    },
    creditoSelecionadoTexto () {
      const opt = this.tiposCreditoOptions.find(o => o.value === this.creditoId)
      return opt ? opt.text : ''
    },
    saldoAtual () {
      const saldo = this.saldos.find(s => Number(s.credito_id) === Number(this.creditoId))
      return saldo ? Number(saldo.saldo) : 0
    },
    textoConfirmar () {
      return `Confirmar concessão de ${this.quantidadeNumero} ${this.quantidadeNumero === 1 ? 'crédito' : 'créditos'}`
    },
  },
  mounted () {
    this.carregarTiposCredito()
  },
  methods: {
    /**
     * Reinicia o formulário. Chamado pelo pai ao abrir o modal.
     */
    iniciar (schemaInicial = '') {
      this.etapa = 'formulario'
      this.quantidade = 1
      this.motivo = ''
      this.motivoTocado = false
      this.erroApi = ''
      this.enviando = false
      this.saldos = []
      this.schema = ''
      if (schemaInicial) {
        this.onSelecionarCfc(schemaInicial)
      }
    },
    async carregarTiposCredito () {
      try {
        const response = await this.$http.get(`${urlApi}/creditos/`)
        this.tiposCreditoOptions = response.body.data.map(credito => ({
          value: credito.id,
          text: credito.descricao,
        }))
        if (this.tiposCreditoOptions.length === 1) {
          this.creditoId = this.tiposCreditoOptions[0].value
        }
      } catch (err) {
        this.erroApi = 'Não foi possível carregar os tipos de crédito.'
      }
    },
    async onSelecionarCfc (schema) {
      this.schema = schema
      this.saldos = []
      this.erroApi = ''
      if (!schema) return
      this.carregandoSaldo = true
      try {
        const response = await this.$http.get(`${urlApi}/creditos/saldo`, { params: { schema } })
        // descarta respostas de uma seleção anterior
        if (this.schema === schema) {
          this.saldos = response.body.data || []
        }
      } catch (err) {
        this.erroApi = (err.body && err.body.message) || 'Não foi possível carregar o saldo da autoescola.'
      } finally {
        if (this.schema === schema) {
          this.carregandoSaldo = false
        }
      }
    },
    alterarQuantidade (delta) {
      const atual = Number.isInteger(this.quantidadeNumero) ? this.quantidadeNumero : 0
      this.quantidade = Math.min(this.maxConcessao, Math.max(1, atual + delta))
    },
    aplicarSugestao (sugestao) {
      this.motivo = this.motivoLimpo ? `${sugestao} - ${this.motivoLimpo}` : `${sugestao} - `
      this.motivoTocado = false
      this.$nextTick(() => {
        const el = document.getElementById('concessao-motivo')
        if (el) {
          el.focus()
          el.setSelectionRange(el.value.length, el.value.length)
        }
      })
    },
    revisar () {
      this.motivoTocado = true
      if (!this.formularioValido) return
      this.erroApi = ''
      this.etapa = 'confirmacao'
    },
    voltar () {
      this.erroApi = ''
      this.etapa = 'formulario'
    },
    async confirmar () {
      if (this.enviando) return
      this.enviando = true
      this.erroApi = ''
      try {
        const response = await this.$http.post(`${urlApi}/creditos/concessao`, {
          schema: this.schema,
          credito_id: this.creditoId,
          quantidade: this.quantidadeNumero,
          motivo: this.motivoLimpo,
        })
        this.$emit('concedido', { ...response.body.data, schema: this.schema })
      } catch (err) {
        this.erroApi = (err.body && err.body.message) || 'Não foi possível conceder os créditos. Tente novamente.'
      } finally {
        this.enviando = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.conceder-creditos {
  padding: 1.5rem;
}
.saldo-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  &__cabecalho {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  &__nome {
    font-weight: 600;
    color: #222;
  }
  &__saldo {
    margin-top: 0.25rem;
    color: #444;
  }
  &__loading {
    margin-top: 0.25rem;
  }
}
.campo {
  display: flex;
  flex-direction: column;
  &__legenda {
    font-weight: 500;
    margin-bottom: 0.35rem;
    color: #222;
  }
  &__ajuda {
    color: #6c757d;
    margin-top: 0.25rem;
  }
  &__erro {
    color: #dc3545;
    margin-top: 0.25rem;
  }
}
.quantidade {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #ced4da;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
  &--erro {
    border-color: #dc3545;
  }
  &__btn {
    width: 40px;
    border: none;
    background: #f1f3f5;
    font-size: 1.2rem;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
  &__input {
    width: 90px;
    border: none;
    text-align: center;
    font-size: 1.1rem;
    padding: 0.4rem;
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
.sugestoes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  &__chip {
    border: 1px solid #ced4da;
    background: #fff;
    border-radius: 999px;
    padding: 0.2rem 0.75rem;
    font-size: 0.85rem;
    cursor: pointer;
    &:hover:not(:disabled) {
      background: #e9ecef;
    }
  }
}
.motivo {
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  resize: vertical;
  &--erro {
    border-color: #dc3545;
  }
  &__rodape {
    display: flex;
    justify-content: space-between;
  }
}
.previa {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  background: #e8f7f2;
  color: #1d6b55;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  &__seta {
    font-weight: 700;
  }
  &__delta {
    font-weight: 600;
  }
}
.erro-api {
  color: #dc3545;
  background: #fdecee;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}
.acoes {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.confirmacao__titulo {
  margin-bottom: 1rem;
}
.resumo {
  margin: 0;
  &__item {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #eee;
    dt {
      font-weight: 500;
      color: #6c757d;
    }
    dd {
      margin: 0;
      text-align: right;
    }
    &--bloco {
      flex-direction: column;
      dd {
        text-align: left;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }
}
.aviso {
  margin-top: 1rem;
  color: #856404;
  background: #fff3cd;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
}
</style>
